import { useEffect } from "react";
import { useAppStore } from "@/store/utils/hooks";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteType } from "@/components/note/types";

/**
 * Hook that shows ghost text from upcoming calendar events when a note is empty.
 */
export function useGhostTextForCalendarEvent(editorStore: MemCommonEditorStore | undefined) {
  const { store } = useAppStore();
  const isCalendarIntegrationEnabled =
    store.spaceAccountFeatureFlags.getFlagStatus("calendar-integration");

  useEffect(
    () => {
      if (!isCalendarIntegrationEnabled) return;

      if (!editorStore) return;
      if (!editorStore.editorContentRendered) return;
      if (editorStore?.noteType === NoteType.Template) return;

      const isNoteEmpty = editorStore.isNoteEmpty;
      if (!isNoteEmpty) {
        editorStore.clearGhostText();
        return;
      }

      const nextUpcomingEventWithNoNote =
        store.spaceAccountCalendarEvents.nextUpcomingEventWithNoNote;
      if (!nextUpcomingEventWithNoNote) return;

      const showGhostText = async () => {
        const result = await nextUpcomingEventWithNoNote.getTemplateIdAndEncodedContentAsync();
        if (!result?.encodedContent) return;

        editorStore.showGhostText({
          encodedContent: result.encodedContent,
          onAccepted: () => {
            store.notes.linkNoteToCalendarEvent({
              noteId: editorStore.id,
              calendarEventId: nextUpcomingEventWithNoNote.id,
            });
          },
        });
      };

      void showGhostText();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      editorStore?.editorContentRendered,
      editorStore?.isNoteEmpty,
      store.spaceAccountCalendarEvents.nextUpcomingEventWithNoNote,
    ]
  );
}
